import React from 'react';

import { Hero } from '@/administration/pages/Journey/common/molecules/JourneyHero';
import { useCurrentUserAccess } from '@/store/currentUser/userCurrentUserAccess';
import { StartProgressOverview } from '@/features/course/learner/atoms/StartProgressOverview';
import { StartAdminButtonGroup } from '@/features/course/learner/atoms/StartAdminButtonGroup';
import { StartDescription } from '@/features/course/learner/atoms/StartDescription';
import { StartLayout } from '@/features/course/learner/atoms/StartLayout/StartLayout';
import { Button } from '@/ui/Button';
import { UsePracticalAssessmentViewerHook } from '@/features/course/learner/hooks/usePracticalAssessmentViewer';
import { LearningJourneyItem } from '@/store/v2/journeys/journey';
import { StartUpNext } from '@/features/course/learner/atoms/StartUpNext/StartUpNext';
import { RLearningContentProvider } from '@/store/v2/remote.gen';
import { LearningTypeLabel } from '@/ui/Label';
import { slugify } from '@/utils/misc';
import { useLearningJourneyBreadcrumbs } from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { useBackToJourneyHref } from '@/features/course/learner/hooks/useBackToJourneyHref';

type PracticalAssessmentStartProps = ReturnType<UsePracticalAssessmentViewerHook> & {
  nextItem?: LearningJourneyItem;
  allUpcomingItemsLocked?: boolean;
  allUpcomingItemsUnlockAt?: Date | string;
};

export const PracticalAssessmentStart = (props: PracticalAssessmentStartProps): JSX.Element => {
  const { isAdmin, loading } = useCurrentUserAccess();
  const displayAdminButtonGroup = !loading && isAdmin;

  const isLoading = props.loadingLearning || !props.loadingProgressInitialized;

  const learningJourneyBreadcrumbs = useLearningJourneyBreadcrumbs({
    id: Number(props.practicalAssessment?.id),
  });

  const backToJourneyHref = useBackToJourneyHref({ id: Number(props.practicalAssessment?.id) });

  if (isLoading || !props.practicalAssessment) {
    return (
      <StartLayout
        hero={<Hero.LoadingState />}
        main={<StartDescription.LoadingState />}
        sidebar={<StartProgressOverview.LoadingState />}
      />
    );
  }

  return (
    <StartLayout
      headerStart={
        learningJourneyBreadcrumbs ? (
          <Button to={backToJourneyHref} variant="ghost" icon={['fas', 'arrow-left']} $iconPosition="start" size="large">
            Back to journey
          </Button>
        ) : (
          <></>
        )
      }
      headerEnd={
        displayAdminButtonGroup ? (
          <StartAdminButtonGroup
            editUrl={`/administration/catalog/assessments/${props?.practicalAssessment?.id}/${slugify(
              props.practicalAssessment.title,
            )}`}
            course={{ id: Number(props.practicalAssessment.id), title: props.practicalAssessment.title }}
          />
        ) : (
          <></>
        )
      }
      hero={
        <Hero
          title={props.practicalAssessment.title}
          imageUrl={props.practicalAssessment.image}
          provider={RLearningContentProvider.Collegial}
        />
      }
      main={
        <>
          <StartDescription headline="Assessment description" description={props.practicalAssessment.teaser} />
          <div>
            <LearningTypeLabel type="PRACTICAL_ASSESSMENT" text="Assessment" />
          </div>
        </>
      }
      sidebar={
        <>
          <StartProgressOverview
            course={{
              effortHours: props.practicalAssessment.effortHours,
              effortMinutes: props.practicalAssessment.effortMinutes,
            }}
            progress={props.progress}
            onStart={props.startAssessment}
            startLoading={props.loadingStartAssessment}
            onContinue={props.onContinuePracticalAssessment}
            onRevisit={props.onRevisitPracticalAssessment}
            startedHeadline="Assessment progress"
          />
          {!!props.progress?.isCompleted && (!!props.nextItem || props.allUpcomingItemsLocked) && (
            <StartUpNext
              learningCardProps={
                props.nextItem
                  ? {
                      objectId: props.nextItem.contentId,
                      title: props.nextItem.content?.title,
                      imageUrl: props.nextItem.content?.imageURL,
                      type: props.nextItem.content?.metadata?.type,
                      level: props.nextItem.content?.metadata?.level,
                      totalEffort: props.nextItem.content?.plannedDuration,
                    }
                  : undefined
              }
              actionHref={
                props.nextItem?.content?.curations?.[0]?.launchURLPath
                  ? `${props.nextItem.content.curations[0].launchURLPath}`
                  : '#'
              }
              parentJourneyId={learningJourneyBreadcrumbs?.journeyId}
              allUpcomingItemsLocked={props.allUpcomingItemsLocked}
              unlockAt={props.allUpcomingItemsUnlockAt}
            />
          )}
        </>
      }
    />
  );
};
